// Planet — the low-poly mint planet at the center of the scene.
// Procedural terrain: the base IcosahedronGeometry is heavily subdivided
// (detail=6 -> ~10242 vertices) and each vertex's height is computed
// from 3D value-noise so the surface reads as faceted continents,
// oceans, beaches, hills, and snow caps. Vertex colors are computed
// from the height band (deep ocean -> shallow -> beach -> forest ->
// hills -> snow). The material is MeshStandardMaterial with
// vertexColors + flatShading so each triangle reads as its own little
// low-poly facet.
//
// We pick colors that respect the design brief's mint + warm-white
// palette — no neon, no high-poly blobs.
//
// LOD: at very high detail (10242 vertices) the planet is the most
// expensive mesh in the scene. We use Drei's <Detailed> to swap in a
// mid (detail=4 → ~2562 vertices) and low (detail=2 → ~162 vertices)
// version as the camera pulls back. Switching happens at distance
// thresholds of 8 and 14 — distances small enough that the visual
// silhouette doesn't change at typical camera positions, but big
// enough that the high-detail geometry only renders when we can
// actually see the difference.

import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { Group, Mesh } from 'three';

import { getPlanetRotation, sceneAtProgress } from './sceneState';
import { SCENES } from './planetSections';
import { useReducedMotion } from '../three/useReducedMotion';
import { useWebGLAvailable } from '../three/useWebGLAvailable';

const PLANET_RADIUS = 1.5;

// LOD thresholds (world-space distance from camera to planet center).
// Below 8  → high detail (detail=6)
// 8..14   → mid detail (detail=4)
// above 14 → low detail (detail=2)
const PLANET_DETAIL_HIGH = 6;
const PLANET_DETAIL_MID = 4;
const PLANET_DETAIL_LOW = 2;
// LOD thresholds (kept inline; manual visibility toggle, not Drei Detailed).
const LOD_NEAR = 8;
const LOD_MID = 14;

// Heightmap controls — tuned so most vertices sit at the beach/forest
// band, with a real minority in deep ocean / snow caps.
const HEIGHT_AMP = 0.31; // total displacement range
const OCEAN_LEVEL = 0.07;
const BEACH_LEVEL = 0.18;
const HILLS_LEVEL = 0.43;
const PEAK_LEVEL = 0.64;

const WIREFRAME_SCALE = 1.016;

// Color anchors — deeper mineral greens with restrained warm highlights.
const COLOR_OCEAN_DEEP = new THREE.Color('#1239ff');
const COLOR_OCEAN_SHALLOW = new THREE.Color('#00c7ff');
const COLOR_BEACH = new THREE.Color('#ffdd00');
const COLOR_FOREST = new THREE.Color('#00c77a');
const COLOR_HILLS = new THREE.Color('#ff5a36');
const COLOR_PEAK = new THREE.Color('#fff3df');
const COLOR_MINERAL = new THREE.Color('#ff2d95');
const COLOR_GOLD_DUST = new THREE.Color('#ffed00');
const COLOR_WIRE = new THREE.Color('#00e5ff');
const COLOR_EMISSIVE = new THREE.Color('#1239ff');

// ---------------------------------------------------------------------------
// 3D value-noise — hand-rolled, deterministic. Lattice of unit-cube
// cells; within a cell we trilinear-interpolate the corners using
// smoothed hash values. FBM (fractal brownian motion) layers several
// octaves so the terrain has both big continents (low freq) and small
// ridges (high freq). Result is normalized to roughly [-1, 1].
// ---------------------------------------------------------------------------

function hash3(x: number, y: number, z: number): number {
  // Standard sin-based hash, integer-friendly.
  let h = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453;
  return h - Math.floor(h);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

function smoothNoise3(x: number, y: number, z: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const iz = Math.floor(z);
  const fx = x - ix;
  const fy = y - iy;
  const fz = z - iz;
  // Smoothstep weights for trilinear.
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const uz = fz * fz * (3 - 2 * fz);
  const c000 = hash3(ix, iy, iz);
  const c100 = hash3(ix + 1, iy, iz);
  const c010 = hash3(ix, iy + 1, iz);
  const c110 = hash3(ix + 1, iy + 1, iz);
  const c001 = hash3(ix, iy, iz + 1);
  const c101 = hash3(ix + 1, iy, iz + 1);
  const c011 = hash3(ix, iy + 1, iz + 1);
  const c111 = hash3(ix + 1, iy + 1, iz + 1);
  const x00 = lerp(c000, c100, ux);
  const x10 = lerp(c010, c110, ux);
  const x01 = lerp(c001, c101, ux);
  const x11 = lerp(c011, c111, ux);
  const y0 = lerp(x00, x10, uy);
  const y1 = lerp(x01, x11, uy);
  return lerp(y0, y1, uz) * 2 - 1; // remap to ~[-1, 1]
}

/** Fractal Brownian Motion stacked over the smooth noise. */
function fbm3(x: number, y: number, z: number, octaves: number): number {
  let amp = 0.5;
  let freq = 1.0;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += amp * smoothNoise3(x * freq, y * freq, z * freq);
    norm += amp;
    amp *= 0.5;
    freq *= 2.0;
  }
  return sum / norm; // ~[-1, 1]
}

/**
 * Sample a deterministic terrain height at a 3D position. The output
 * is normalized to roughly [0, 1] (we bias via a smoothstep on the
 * raw fbm result so the ocean/land ratio looks right).
 */
function heightAt(vx: number, vy: number, vz: number): number {
  // Scale up so a unit icosahedron vertex sees variation across the
  // full sphere surface.
  const continental = fbm3(vx * 1.55, vy * 1.55, vz * 1.55, 5);
  const foldedRidges = 1 - Math.abs(fbm3(vx * 4.9 + 2.1, vy * 4.9 - 1.4, vz * 4.9, 4));
  const fine = fbm3(vx * 10.5 - 3.1, vy * 10.5 + 1.7, vz * 10.5 + 0.5, 3);
  const raw = continental * 0.82 + (foldedRidges * 2 - 1) * 0.14 + fine * 0.04;
  const t = clamp01((raw + 1) * 0.5);
  // Slightly wider ocean band, then crisp highlands. The result keeps
  // the low-poly silhouette but adds enough geological variation that
  // close shots read as a deliberately modeled surface.
  return smoothstep(0.22, 0.90, t);
}

/** Radius of the rendered terrain beneath a world-space direction.
 *  The planet mesh rotates around Y, so sampling first moves the direction
 *  back into the geometry's local coordinates. */
export function planetSurfaceRadiusAt(
  direction: [number, number, number],
  planetRotation = 0,
): number {
  const world = new THREE.Vector3(...direction).normalize();
  world.applyAxisAngle(new THREE.Vector3(0, 1, 0), -planetRotation);
  const h = heightAt(world.x, world.y, world.z);
  const ridge = smoothstep(
    0.5,
    0.94,
    1 - Math.abs(fbm3(world.x * 7.5 + 0.4, world.y * 7.5 - 2.2, world.z * 7.5 + 1.6, 3)),
  );
  const fracture = smoothstep(
    0.66,
    0.96,
    (fbm3(world.x * 15 - 6, world.y * 15 + 4, world.z * 15, 2) + 1) * 0.5,
  );
  return PLANET_RADIUS + HEIGHT_AMP * (h + ridge * 0.035 + fracture * 0.012);
}

/** Project an authored direction onto the actual procedural terrain. */
export function projectToPlanetSurface(
  direction: [number, number, number],
  planetRotation = 0,
  inset = 0,
): [number, number, number] {
  const point = new THREE.Vector3(...direction).normalize();
  point.multiplyScalar(planetSurfaceRadiusAt(direction, planetRotation) + inset);
  return [point.x, point.y, point.z];
}

/** Pick a vertex color from the height band. */
function colorFromHeight(h: number, out: THREE.Color): THREE.Color {
  if (h < OCEAN_LEVEL) {
    // Deep -> shallow blend.
    const t = h / OCEAN_LEVEL;
    return out.copy(COLOR_OCEAN_DEEP).lerp(COLOR_OCEAN_SHALLOW, t);
  }
  if (h < BEACH_LEVEL) {
    const t = (h - OCEAN_LEVEL) / (BEACH_LEVEL - OCEAN_LEVEL);
    return out.copy(COLOR_OCEAN_SHALLOW).lerp(COLOR_BEACH, t);
  }
  if (h < HILLS_LEVEL) {
    const t = (h - BEACH_LEVEL) / (HILLS_LEVEL - BEACH_LEVEL);
    return out.copy(COLOR_BEACH).lerp(COLOR_FOREST, t);
  }
  if (h < PEAK_LEVEL) {
    const t = (h - HILLS_LEVEL) / (PEAK_LEVEL - HILLS_LEVEL);
    return out.copy(COLOR_FOREST).lerp(COLOR_HILLS, t);
  }
  {
    const t = Math.min(1, (h - PEAK_LEVEL) / (1 - PEAK_LEVEL));
    return out.copy(COLOR_HILLS).lerp(COLOR_PEAK, t);
  }
}

/**
 * Build one level of the planet's procedural terrain: an
 * IcosahedronGeometry, displaced by heightAt(), with per-vertex
 * colors from the height band.
 *
 * The terrain function is deterministic in world position, so every
 * level reproduces the same continents in the same places — just at
 * different resolutions. We run the displacement for each level so
 * the shape is identical at every scale.
 */
function buildPlanetGeometry(detail: number): THREE.BufferGeometry {
  const geo = new THREE.IcosahedronGeometry(PLANET_RADIUS, detail);
  const positions = geo.attributes.position;
  const colors = new Float32Array(positions.count * 3);
  const tmp = new THREE.Color();

  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);

    // Re-normalize the direction so we push along the radial vector.
    const len = Math.sqrt(x * x + y * y + z * z) || 1;
    const nx = x / len;
    const ny = y / len;
    const nz = z / len;

    const h = heightAt(nx, ny, nz);
    const ridge = smoothstep(
      0.50,
      0.94,
      1 - Math.abs(fbm3(nx * 7.5 + 0.4, ny * 7.5 - 2.2, nz * 7.5 + 1.6, 3)),
    );
    const fracture = smoothstep(
      0.66,
      0.96,
      (fbm3(nx * 15.0 - 6.0, ny * 15.0 + 4.0, nz * 15.0, 2) + 1) * 0.5,
    );
    const r = PLANET_RADIUS + HEIGHT_AMP * (h + ridge * 0.035 + fracture * 0.012);
    positions.setXYZ(i, nx * r, ny * r, nz * r);

    colorFromHeight(h, tmp);
    const mineral = ridge * smoothstep(BEACH_LEVEL, 0.84, h) * 0.18;
    const warmFacet = fracture * smoothstep(HILLS_LEVEL, 1.0, h) * 0.12;
    const shade = 0.88 + 0.12 * smoothstep(-0.25, 0.95, ny) + 0.04 * fracture;
    tmp.lerp(COLOR_MINERAL, mineral).lerp(COLOR_GOLD_DUST, warmFacet);
    tmp.multiplyScalar(shade);
    colors[i * 3 + 0] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
  }

  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();
  return geo;
}

export interface PlanetProps {
  /**
   * External rotation delta (from drag hook). Added on top of the
   * scroll-driven rotation. Sign: positive Y = planet spins right.
   */
  dragDeltaRef?: React.MutableRefObject<number>;
  /**
   * While the user is dragging, the scroll-driven rotation pauses.
   * The autorotate also pauses so the gesture feels responsive.
   */
  isDraggingRef?: React.MutableRefObject<boolean>;
  /**
   * Click handler — set the open panel id on the planet body. The
   * scene-wide click handler is on SceneLandmarks; the planet is a
   * fall-through target that just pulses on click.
   */
  onPlanetClick?: () => void;
}

/**
 * Render a single planet mesh + wireframe shell. Used as a child of
 * Drei's <Detailed> for one LOD level.
 */
function PlanetLevel({
  detail,
  meshRef,
  wireRef,
  onClick,
  hasWebGL,
}: {
  detail: number;
  meshRef: React.MutableRefObject<Mesh | null>;
  wireRef: React.MutableRefObject<Mesh | null>;
  onClick: (e: { stopPropagation: () => void }) => void;
  hasWebGL: boolean;
}) {
  // Cache one geometry per detail level, so the Detailed wrapper
  // picks up a stable BufferGeometry instance for each LOD.
  const geometry = useMemo(() => buildPlanetGeometry(detail), [detail]);
  return (
    <group name={`planet-level-${detail}`}>
      <mesh
        ref={meshRef}
        name={`planet-mesh-${detail}`}
        geometry={geometry}
        onClick={onClick}
        raycast={hasWebGL ? undefined : () => null}
        frustumCulled
      >
        <meshStandardMaterial
          color={COLOR_PEAK}
          vertexColors
          flatShading
          roughness={0.66}
          metalness={0.08}
          emissive={COLOR_EMISSIVE}
          emissiveIntensity={0.06}
        />
      </mesh>

      {/* Wireframe overlay — slightly larger shell for the
          "atmospheric edge" feel. Sits at low opacity, mint color. Not
          pickable. */}
      <mesh
        ref={wireRef}
        name={`planet-wire-${detail}`}
        geometry={geometry}
        scale={WIREFRAME_SCALE}
        raycast={() => null}
        frustumCulled
      >
        <meshBasicMaterial
          color={COLOR_WIRE}
          wireframe
          transparent
          opacity={0.09}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

export function Planet({
  dragDeltaRef,
  isDraggingRef,
  onPlanetClick,
}: PlanetProps) {
  const groupRef = useRef<Group | null>(null);
  // One mesh+wire pair of refs per detail level. Only the visible
  // level's wireframe is actually rendered, but the pulse animation
  // applies to the whole group so we don't need per-level scale
  // gymnastics.
  const meshHighRef = useRef<Mesh | null>(null);
  const wireHighRef = useRef<Mesh | null>(null);
  const meshMidRef = useRef<Mesh | null>(null);
  const wireMidRef = useRef<Mesh | null>(null);
  const meshLowRef = useRef<Mesh | null>(null);
  const wireLowRef = useRef<Mesh | null>(null);
  const pulseRef = useRef<{ t: number; active: boolean }>({ t: 0, active: false });
  const reduced = useReducedMotion();
  const hasWebGL = useWebGLAvailable();
  // Manual LOD — toggle visibility per detail level based on camera
  // distance. We avoid Drei's <Detailed> because its R3F v8 useEffect
  // reads lod.levels before the ref is attached (undefined.length).
  // See git history — the regression came from the Phase 14 LOD pass.
  const camera = useThree((s) => s.camera);
  const lodStateRef = useRef<{ near: boolean; mid: boolean }>({
    near: true,
    mid: false,
  });
  // Scene-driven planet palette — material.color multiplies vertexColors,
  // so a soft tint shifts the whole mineral surface toward the chapter.
  const tintTarget = useRef(new THREE.Color(SCENES[0].planetTint)).current;
  const emissiveTarget = useRef(new THREE.Color(SCENES[0].planetEmissive)).current;
  const wireTarget = useRef(new THREE.Color(SCENES[0].planetWire)).current;
  const tintScratch = useRef(new THREE.Color()).current;
  const emissiveScratch = useRef(new THREE.Color()).current;
  const wireScratch = useRef(new THREE.Color()).current;

  const applyPlanetPalette = (tint: THREE.Color, emissive: THREE.Color, wire: THREE.Color) => {
    const meshes = [meshHighRef.current, meshMidRef.current, meshLowRef.current];
    const wires = [wireHighRef.current, wireMidRef.current, wireLowRef.current];
    // Soft multiply: keep vertexColors readable, bias toward the chapter tint
    // without crushing channels into mud.
    tintScratch.setRGB(1, 1, 1).lerp(tint, 0.62);
    for (const mesh of meshes) {
      if (!mesh) continue;
      const mat = mesh.material as THREE.MeshStandardMaterial;
      mat.color.lerp(tintScratch, 0.12);
      mat.emissive.lerp(emissive, 0.12);
      mat.emissiveIntensity = THREE.MathUtils.lerp(mat.emissiveIntensity, 0.16, 0.12);
    }
    for (const mesh of wires) {
      if (!mesh) continue;
      const mat = mesh.material as THREE.MeshBasicMaterial;
      mat.color.lerp(wire, 0.12);
      mat.opacity = THREE.MathUtils.lerp(mat.opacity, 0.075, 0.1);
    }
  };

  // -------------------------------------------------------------------------
  // Frame loop — scroll-driven rotation + autorotate + click pulse + tint.
  // -------------------------------------------------------------------------
  useFrame((_, delta) => {
    if (!groupRef.current) return;
    const dt = Math.min(delta, 1 / 30);
    const p =
      typeof window !== 'undefined' && typeof window.__planetProgress === 'number'
        ? window.__planetProgress
        : 0;
    const target = getPlanetRotation(p);

    // Scene palette — blend toward the next chapter near the boundary so
    // the planet's mineral wash changes with the scroll, not as a snap.
    const { idx, blendIn } = sceneAtProgress(p);
    const scene = SCENES[idx] ?? SCENES[0];
    const nextScene = SCENES[Math.min(idx + 1, SCENES.length - 1)];
    const edge = blendIn > 0.72 ? (blendIn - 0.72) / 0.28 : 0;
    tintTarget.set(scene.planetTint);
    emissiveTarget.set(scene.planetEmissive);
    wireTarget.set(scene.planetWire);
    if (edge > 0 && idx < SCENES.length - 1) {
      tintTarget.lerp(tintScratch.set(nextScene.planetTint), edge);
      emissiveTarget.lerp(emissiveScratch.set(nextScene.planetEmissive), edge);
      wireTarget.lerp(wireScratch.set(nextScene.planetWire), edge);
    }
    applyPlanetPalette(tintTarget, emissiveTarget, wireTarget);

    // 1) Scroll-driven rotation. Lerp toward target so the planet never
    //    jumps when the user scrolls fast.
    const curRot = groupRef.current.rotation.y;
    const scrollDriven = curRot + (target - curRot) * 0.1;

    // 2) Drag delta — additive on top of scroll. Drag pauses autorotate.
    const dragDelta = dragDeltaRef?.current ?? 0;
    const isDragging = isDraggingRef?.current ?? false;

    // 3) Slow autorotate (additive, only when not dragging).
    const autorotate = isDragging || reduced.current ? 0 : dt * 0.05;

    groupRef.current.rotation.y = scrollDriven + dragDelta + autorotate;

    // 5) Manual LOD: toggle which planet mesh is visible based on
    //    the camera's distance from origin. We only swap visibility,
    //    never recreate geometry, so there is no popping or hitches.
    const dist = camera.position.length();
    const nearVisible = dist < LOD_NEAR;
    const midVisible = !nearVisible && dist < LOD_MID;
    if (lodStateRef.current.near !== nearVisible) {
      lodStateRef.current.near = nearVisible;
      lodStateRef.current.mid = midVisible;
      if (meshHighRef.current) meshHighRef.current.visible = nearVisible;
      if (wireHighRef.current) wireHighRef.current.visible = nearVisible;
      if (meshMidRef.current) meshMidRef.current.visible = midVisible;
      if (wireMidRef.current) wireMidRef.current.visible = midVisible;
      if (meshLowRef.current) meshLowRef.current.visible = !nearVisible && !midVisible;
      if (wireLowRef.current) wireLowRef.current.visible = !nearVisible && !midVisible;
    }

    // 4) Click pulse — spring-ish scale 1 -> 1.05 -> 1. Applied to
    //    the whole planet group so all detail levels pulse together.
    //    (LOD swap means only one level is visible at a time, so
    //    we don't need to track which mesh is currently shown.)
    if (pulseRef.current.active) {
      pulseRef.current.t += dt;
      const T = 0.6; // 600ms total
      const k = pulseRef.current.t / T;
      // Damped sine: 0..1..0 with overshoot baked into the ease.
      const phase = Math.sin(k * Math.PI); // 0 -> 1 -> 0
      const scale = 1 + phase * 0.05;
      if (groupRef.current) groupRef.current.scale.setScalar(scale);
      if (k >= 1) {
        pulseRef.current.active = false;
        pulseRef.current.t = 0;
        if (groupRef.current) groupRef.current.scale.setScalar(1);
      }
    }
  });

  const handleClick = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    pulseRef.current.t = 0;
    pulseRef.current.active = true;
    onPlanetClick?.();
  };

  return (
    <group ref={groupRef} name="planet" userData={{ pickable: false }}>
      <PlanetLevel
        detail={PLANET_DETAIL_HIGH}
        meshRef={meshHighRef}
        wireRef={wireHighRef}
        onClick={handleClick}
        hasWebGL={hasWebGL}
      />
      <PlanetLevel
        detail={PLANET_DETAIL_MID}
        meshRef={meshMidRef}
        wireRef={wireMidRef}
        onClick={handleClick}
        hasWebGL={hasWebGL}
      />
      <PlanetLevel
        detail={PLANET_DETAIL_LOW}
        meshRef={meshLowRef}
        wireRef={wireLowRef}
        onClick={handleClick}
        hasWebGL={hasWebGL}
      />
    </group>
  );
}

export default Planet;
